/* Capture domain types (epic E4).
 *
 * A *module* is a physical locker column carrying a serial number. Technicians
 * confirm or update those serials on install / removal / repair / swap, and the
 * chain of custody runs manufacturer -> warehouse -> truck -> site -> removal
 * -> warehouse. Serials come back cleanest at pre-flight (99.97% populated)
 * and worst at install (83.5%), so the warehouse bulk path is the one that has
 * to be fast, and the site path is the one that has to be careful.
 *
 * Two rules are baked into these types rather than left to the UI:
 *
 *  1. A scanned capture and a hand-typed capture are THE SAME SHAPE. Downstream
 *     code must not be able to treat one as more trustworthy by accident; if it
 *     wants to know, it reads `method`. Manual entry is a first-class path, not
 *     a degraded one (bad light, damaged labels, gloved hands).
 *  2. `at` is the moment of capture, not the moment of submission or of page
 *     load. The chain of custody is worthless if the timestamp is a load time.
 */

/** How a serial reached the app. Both are equally valid; neither is a fallback. */
export type CaptureMethod = 'scan' | 'manual';

/**
 * One captured module serial.
 *
 * Identical field set for `scan` and `manual` — `format` is the only field that
 * can be null, and it is null precisely because a typed serial has no symbology.
 */
export interface SerialCapture {
  /** Stable per-row id, so the UI can key and remove rows. */
  id: string;
  /** Normalised value. This is what gets written. */
  serial: string;
  /** Exactly what the decoder or the technician produced, pre-normalisation. */
  raw: string;
  method: CaptureMethod;
  /** zxing symbology name (e.g. `Code128`, `DataMatrix`); null for manual entry. */
  format: string | null;
  /** ms since epoch at the moment of capture. */
  at: number;
}

/** Why a candidate never became a capture. */
export type RejectReason =
  | 'empty'
  | 'too-short'
  | 'too-long'
  | 'charset'
  | 'checksum';

/**
 * What just happened, for per-scan feedback. Bulk scanning is unusable without
 * this: the technician needs to know within one beat whether the last wave of
 * the phone landed, was a repeat, or was junk.
 */
export type ScanEvent =
  | { kind: 'accepted'; capture: SerialCapture }
  | {
      kind: 'duplicate';
      serial: string;
      /** id of the capture already holding this serial. */
      ofId: string;
      /** How many times this serial has now been suppressed. */
      count: number;
    }
  | { kind: 'rejected'; raw: string; reason: RejectReason };

/** Immutable bulk-scan session state. */
export interface ScanSessionState {
  /** Accumulation order — first read wins, later reads are suppressed. */
  readonly captures: readonly SerialCapture[];
  /** Total suppressed duplicate reads across the session. Shown in the UI. */
  readonly duplicates: number;
  /** Per-serial suppression counts, for "you already scanned this 3x". */
  readonly duplicatesBySerial: Readonly<Record<string, number>>;
  /** Most recent event, for the feedback line. */
  readonly lastEvent: ScanEvent | null;
}

/** A candidate value offered to the session, from any source. */
export interface SerialCandidate {
  raw: string;
  /** Symbology if this came off the decoder; omit or null for manual entry. */
  format?: string | null;
  method: CaptureMethod;
  at: number;
  id: string;
}

/**
 * Serial shape rules. The symbology and exact format are still an open question
 * (PRD Q-45: does the manufacturer barcode encode module *type* or only a
 * serial?), so these are deliberately loose and injectable rather than a
 * hard-coded regex that would silently drop real serials in the field.
 */
export interface SerialRules {
  minLength: number;
  maxLength: number;
  /** Characters permitted after normalisation. */
  allowed: RegExp;
}

export const DEFAULT_SERIAL_RULES: SerialRules = {
  minLength: 4,
  maxLength: 64,
  allowed: /^[A-Z0-9][A-Z0-9._/-]*$/,
};

/** A photo that has been compressed, stripped and handed to the blob store. */
export interface PhotoAttachment {
  /** Opaque id from `putBlob`. The bytes never live in the outbox item. */
  blobId: string;
  mime: 'image/jpeg';
  /** Compressed size in bytes — surfaced in the UI so huge photos are visible. */
  bytes: number;
  /** Size of what came off the camera or out of the library. */
  originalBytes: number;
  width: number;
  height: number;
  at: number;
  source: 'camera' | 'library';
  /** Which prompt this photo answers, e.g. "Serial plate, module 2". */
  label?: string;
}
